import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    Grid,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography,
    Alert
} from '@mui/material';
import { IconPlus, IconTrash, IconX, IconArrowLeft, IconSend, IconPhotoPlus } from '@tabler/icons';
import api from '../../../../services/api';
import WhatsappInteractivePreviewCard from './WhatsappInteractivePreviewCard';

const BUTTONS_MAX = 3;
const LIST_ROWS_MAX = 10;
const CAROUSEL_CARDS_MAX = 10;
const CAROUSEL_CARD_BUTTONS_MAX = 3;

const CARD_BUTTON_TYPE_OPTIONS = [
    { value: 'REPLAY', label: 'Resposta rápida' },
    { value: 'CALL', label: 'Ligar' },
    { value: 'URL', label: 'Abrir link' }
];

const DEFAULT_FORM = {
    integration: '',
    kind: 'buttons',
    name: '',
    body_text: '',
    buttons: [],
    list_button_text: '',
    list_title: '',
    list_footer_text: '',
    list_sections: [],
    carousel_cards: [],
    delay_message: 15
};

const ViewWhatsappInteractive = () => {
    const [appId, setAppId] = useState(null);
    const [integrations, setIntegrations] = useState([]);
    const [selectedIntegration, setSelectedIntegration] = useState(null);

    const [templates, setTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [loadError, setLoadError] = useState('');

    const [openDialog, setOpenDialog] = useState(false);
    const [form, setForm] = useState(DEFAULT_FORM);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [editingTemplateId, setEditingTemplateId] = useState(null);
    // Arquivos novos de imagem por card do carrossel (índice -> File) -
    // separado de `form.carousel_cards[i].image` (que guarda a URL já
    // existente, se estiver editando, ou fica vazio até o upload).
    const [cardImageFiles, setCardImageFiles] = useState({});

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        loadIntegrations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const loadIntegrations = async () => {
        const res = await api.get(`v1/apps/${appId}/integrations/`);
        setIntegrations((res.data.integrations || res.data || []).filter((i) => i.client_type === 'official' || i.client_type === 'w_api'));
    };

    const loadTemplates = async (integrationId) => {
        setLoadingTemplates(true);
        setLoadError('');
        try {
            const res = await api.get(`v1/apps/${appId}/whatsapp_interactive_templates/?integration_id=${integrationId}`);
            setTemplates(res.data || []);
        } catch (err) {
            setLoadError(err.response?.data?.error || 'Erro ao consultar os conjuntos de botões/lista.');
        } finally {
            setLoadingTemplates(false);
        }
    };

    const handleSelectIntegration = (integration) => {
        setSelectedIntegration(integration);
        loadTemplates(integration.id);
    };

    const handleChangeIntegration = () => {
        setSelectedIntegration(null);
        setTemplates([]);
        setLoadError('');
    };

    const deleteTemplate = async (templateId) => {
        await api.delete(`v1/apps/${appId}/whatsapp_interactive_templates/${templateId}/`);
        loadTemplates(selectedIntegration.id);
    };

    const openCreateDialog = () => {
        setEditingTemplateId(null);
        setForm({ ...DEFAULT_FORM, integration: selectedIntegration?.id || '', kind: 'buttons' });
        setCardImageFiles({});
        setError('');
        setOpenDialog(true);
    };

    const openEditDialog = (template) => {
        setEditingTemplateId(template.id);
        setForm({
            integration: template.integration,
            kind: template.kind,
            name: template.name || '',
            body_text: template.body_text || '',
            buttons: template.buttons || [],
            list_button_text: template.list_button_text || '',
            list_title: template.list_title || '',
            list_footer_text: template.list_footer_text || '',
            list_sections: template.list_sections || [],
            carousel_cards: template.carousel_cards || [],
            delay_message: template.delay_message ?? 15
        });
        setCardImageFiles({});
        setError('');
        setOpenDialog(true);
    };

    // Na edição, o formulário usa a integração do próprio template salvo
    // (não a `selectedIntegration` da tela) - "Integração"/"Tipo" ficam
    // travados durante a edição (ver `save`), então não muda mesmo se por
    // algum motivo divergirem.
    const formIntegration = editingTemplateId ? integrations.find((i) => i.id === form.integration) : selectedIntegration;
    const isWApiForm = formIntegration?.client_type === 'w_api';

    // -------- Botões --------
    // Resposta rápida simples (id+title) - mesma regra pra official e
    // w_api, nenhum dos dois suporta botão de ligar/abrir link fora de
    // Template Message aprovado (ver docs.w-api.app/message/send-button-list).
    const addButton = () => {
        if (form.buttons.length >= BUTTONS_MAX) return;
        setForm({ ...form, buttons: [...form.buttons, { id: `btn_${form.buttons.length + 1}`, title: '' }] });
    };
    const updateButton = (i, patch) => {
        const buttons = [...form.buttons];
        buttons[i] = { ...buttons[i], ...patch };
        setForm({ ...form, buttons });
    };
    const removeButton = (i) => setForm({ ...form, buttons: form.buttons.filter((_, idx) => idx !== i) });

    // -------- Lista (seções/linhas) --------
    const totalRows = form.list_sections.reduce((sum, s) => sum + (s.rows?.length || 0), 0);
    const addSection = () => setForm({ ...form, list_sections: [...form.list_sections, { title: '', rows: [] }] });
    const updateSection = (si, patch) => {
        const sections = [...form.list_sections];
        sections[si] = { ...sections[si], ...patch };
        setForm({ ...form, list_sections: sections });
    };
    const removeSection = (si) => setForm({ ...form, list_sections: form.list_sections.filter((_, idx) => idx !== si) });
    const addRow = (si) => {
        if (totalRows >= LIST_ROWS_MAX) return;
        const sections = [...form.list_sections];
        const rows = [...(sections[si].rows || [])];
        rows.push({ id: `row_${si}_${rows.length + 1}`, title: '', description: '' });
        sections[si] = { ...sections[si], rows };
        setForm({ ...form, list_sections: sections });
    };
    const updateRow = (si, ri, patch) => {
        const sections = [...form.list_sections];
        const rows = [...sections[si].rows];
        rows[ri] = { ...rows[ri], ...patch };
        sections[si] = { ...sections[si], rows };
        setForm({ ...form, list_sections: sections });
    };
    const removeRow = (si, ri) => {
        const sections = [...form.list_sections];
        sections[si] = { ...sections[si], rows: sections[si].rows.filter((_, idx) => idx !== ri) };
        setForm({ ...form, list_sections: sections });
    };

    // -------- Carrossel (cards) --------
    // Diferente dos botões de "kind=buttons", os botões DENTRO de um card
    // de carrossel suportam de verdade CALL (ligar)/URL (abrir link),
    // além de resposta rápida - confirmado com envio real.
    const addCard = () => {
        if (form.carousel_cards.length >= CAROUSEL_CARDS_MAX) return;
        setForm({ ...form, carousel_cards: [...form.carousel_cards, { text: '', image: '', buttons: [] }] });
    };
    const updateCard = (ci, patch) => {
        const cards = [...form.carousel_cards];
        cards[ci] = { ...cards[ci], ...patch };
        setForm({ ...form, carousel_cards: cards });
    };
    const removeCard = (ci) => {
        setForm({ ...form, carousel_cards: form.carousel_cards.filter((_, idx) => idx !== ci) });
        setCardImageFiles((prev) => {
            const copy = { ...prev };
            delete copy[ci];
            return copy;
        });
    };
    const handleCardImageUpload = (ci, file) => {
        if (!file) return;
        setCardImageFiles((prev) => ({ ...prev, [ci]: file }));
    };
    const addCardButton = (ci) => {
        const card = form.carousel_cards[ci];
        if ((card.buttons || []).length >= CAROUSEL_CARD_BUTTONS_MAX) return;
        updateCard(ci, { buttons: [...(card.buttons || []), { type: 'REPLAY', buttonText: '' }] });
    };
    const updateCardButton = (ci, bi, patch) => {
        const card = form.carousel_cards[ci];
        const buttons = [...(card.buttons || [])];
        buttons[bi] = { ...buttons[bi], ...patch };
        updateCard(ci, { buttons });
    };
    const removeCardButton = (ci, bi) => {
        const card = form.carousel_cards[ci];
        updateCard(ci, { buttons: (card.buttons || []).filter((_, idx) => idx !== bi) });
    };

    const save = async () => {
        setSaving(true);
        setError('');
        try {
            const formData = new FormData();
            formData.append('integration', form.integration);
            formData.append('name', form.name);
            formData.append('kind', form.kind);
            formData.append('body_text', form.body_text);

            if (form.kind === 'buttons') {
                formData.append('buttons', JSON.stringify(form.buttons));
            } else if (form.kind === 'list') {
                formData.append('list_button_text', form.list_button_text);
                formData.append('list_sections', JSON.stringify(form.list_sections));
                if (isWApiForm) {
                    formData.append('list_title', form.list_title);
                    formData.append('list_footer_text', form.list_footer_text);
                }
            } else {
                // carrossel - "image" no JSON é a URL já existente (se
                // estiver editando); se tiver um arquivo novo pra esse
                // índice, o backend troca pela URL do upload automaticamente.
                formData.append('carousel_cards', JSON.stringify(form.carousel_cards));
                Object.entries(cardImageFiles).forEach(([index, file]) => {
                    formData.append(`card_image_${index}`, file);
                });
            }
            if (isWApiForm) formData.append('delay_message', form.delay_message);

            const res = editingTemplateId
                ? await api.patch(`v1/apps/${appId}/whatsapp_interactive_templates/${editingTemplateId}/`, formData, {
                      headers: { 'Content-Type': 'multipart/form-data' }
                  })
                : await api.post(`v1/apps/${appId}/whatsapp_interactive_templates/`, formData, {
                      headers: { 'Content-Type': 'multipart/form-data' }
                  });
            if (res.data.error) {
                setError(res.data.error);
            } else {
                setOpenDialog(false);
                loadTemplates(selectedIntegration.id);
            }
        } catch (err) {
            setError(
                err.response?.data?.error || `Erro ao ${editingTemplateId ? 'salvar as alterações' : 'criar'} o conjunto de botões/lista.`
            );
        } finally {
            setSaving(false);
        }
    };

    const cardHasImage = (card, index) => Boolean(card.image) || Boolean(cardImageFiles[index]);
    const cardButtonValid = (b) => b.buttonText && (b.type !== 'CALL' || b.phone) && (b.type !== 'URL' || b.url);

    const canSave =
        !saving &&
        form.name &&
        form.body_text &&
        form.integration &&
        (form.kind === 'buttons'
            ? form.buttons.length > 0 && form.buttons.every((b) => b.title)
            : form.kind === 'list'
              ? form.list_button_text && form.list_sections.length > 0 && totalRows > 0
              : form.carousel_cards.length > 0 &&
                form.carousel_cards.every(
                    (card, ci) =>
                        card.text && cardHasImage(card, ci) && (card.buttons || []).length > 0 && card.buttons.every(cardButtonValid)
                ));

    const draftTemplate = { ...form };

    return (
        <Box p={4} sx={{ minHeight: '100vh' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                <Box>
                    <Typography variant="h4" fontWeight="700">
                        Botões e Listas do WhatsApp
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {selectedIntegration
                            ? `Conjuntos salvos pra "${selectedIntegration.displayName}"`
                            : 'Selecione a integração pra ver os conjuntos de botões/lista dessa conta'}
                    </Typography>
                </Box>

                {selectedIntegration && (
                    <Stack direction="row" spacing={1}>
                        <Button variant="outlined" onClick={handleChangeIntegration}>
                            Trocar integração
                        </Button>
                        <Button variant="contained" startIcon={<IconPlus size={18} />} onClick={openCreateDialog}>
                            Criar botões/lista
                        </Button>
                    </Stack>
                )}
            </Stack>

            {!selectedIntegration && (
                <Grid container spacing={2}>
                    {integrations.map((integration) => (
                        <Grid item xs={12} sm={6} md={4} key={integration.id}>
                            <Card sx={{ borderRadius: 3, cursor: 'pointer' }} onClick={() => handleSelectIntegration(integration)}>
                                <CardContent>
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                        <Typography variant="subtitle1" fontWeight="700">
                                            {integration.displayName}
                                        </Typography>
                                        <Chip
                                            size="small"
                                            label={integration.client_type === 'official' ? 'Oficial' : 'Não Oficial'}
                                            color={integration.client_type === 'official' ? 'success' : 'default'}
                                        />
                                    </Stack>
                                    {integration.phoneNumber && (
                                        <Typography variant="body2" color="text.secondary">
                                            {integration.phoneNumber}
                                        </Typography>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                    {integrations.length === 0 && (
                        <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                                Nenhuma integração WhatsApp Oficial ou Não oficial cadastrada ainda.
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            )}

            {selectedIntegration && (
                <>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Pra mandar um desses pelo endpoint de envio, copie o ID do card (ícone de copiar) e passe como{' '}
                        <code>{'{"template_id": "..."}'}</code> no body da requisição - se editar o conjunto aqui depois, o mesmo ID já
                        manda a versão nova automaticamente.
                    </Alert>

                    {loadError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {loadError}
                        </Alert>
                    )}

                    {loadingTemplates ? (
                        <Box display="flex" justifyContent="center" py={4}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : (
                        <Grid container spacing={3}>
                            {templates.map((t) => (
                                <Grid item xs={12} sm={6} md={4} key={t.id}>
                                    <WhatsappInteractivePreviewCard template={t} onEdit={openEditDialog} onDelete={deleteTemplate} />
                                </Grid>
                            ))}
                            {templates.length === 0 && (
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">
                                        Nenhum conjunto de botões/lista criado ainda pra essa conta.
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </>
            )}

            {/* CRIAR - tela cheia, formulário + prévia ao vivo */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullScreen>
                <Box sx={{ display: 'flex', height: '100%', background: '#f8fafc' }}>
                    <Box sx={{ flex: 1, overflow: 'auto', p: 4 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                            <Box>
                                <Button
                                    variant="outlined"
                                    startIcon={<IconArrowLeft size={18} />}
                                    onClick={() => setOpenDialog(false)}
                                    sx={{ borderRadius: '12px', textTransform: 'none' }}
                                >
                                    Voltar
                                </Button>
                                <Typography variant="h3" sx={{ fontWeight: 900, mt: 2 }}>
                                    {editingTemplateId ? 'Editar botões/lista' : 'Criar botões/lista'}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Mensagem interativa livre (dentro da janela de 24h) - não passa por aprovação da Meta.
                                    {editingTemplateId && ' O ID continua o mesmo depois de salvar.'}
                                </Typography>
                            </Box>
                            <IconButton onClick={() => setOpenDialog(false)}>
                                <IconX />
                            </IconButton>
                        </Box>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Integração"
                                    disabled={!!editingTemplateId}
                                    helperText={editingTemplateId ? 'Não pode trocar a integração ao editar' : ''}
                                    value={form.integration}
                                    onChange={(e) => setForm({ ...form, integration: e.target.value })}
                                >
                                    {integrations.map((i) => (
                                        <MenuItem key={i.id} value={i.id}>
                                            {i.displayName} {i.client_type === 'w_api' ? 'Não Oficial' : '(Oficial)'}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Nome do conjunto"
                                    helperText="Só pra identificar na lista (ex: confirmar_pedido)"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Button
                                        variant={form.kind === 'buttons' ? 'contained' : 'outlined'}
                                        disabled={!!editingTemplateId}
                                        onClick={() => setForm({ ...form, kind: 'buttons' })}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Botões
                                    </Button>
                                    <Button
                                        variant={form.kind === 'list' ? 'contained' : 'outlined'}
                                        disabled={!!editingTemplateId}
                                        onClick={() => setForm({ ...form, kind: 'list' })}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Lista
                                    </Button>
                                    <Tooltip title={!isWApiForm ? 'Carrossel só é suportado na API Não Oficial' : ''}>
                                        <span>
                                            <Button
                                                variant={form.kind === 'carousel' ? 'contained' : 'outlined'}
                                                disabled={!isWApiForm || !!editingTemplateId}
                                                onClick={() => setForm({ ...form, kind: 'carousel' })}
                                                sx={{ textTransform: 'none' }}
                                            >
                                                Carrossel
                                            </Button>
                                        </span>
                                    </Tooltip>
                                    {editingTemplateId && (
                                        <Typography variant="caption" color="text.secondary">
                                            Não pode trocar o tipo ao editar
                                        </Typography>
                                    )}
                                </Stack>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    label="Corpo da mensagem"
                                    helperText="Texto que acompanha os botões/lista"
                                    value={form.body_text}
                                    onChange={(e) => setForm({ ...form, body_text: e.target.value })}
                                />
                            </Grid>

                            {isWApiForm && (
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Delay antes de enviar (segundos)"
                                        helperText="Entre 1 e 15 segundos"
                                        value={form.delay_message}
                                        onChange={(e) => setForm({ ...form, delay_message: Number(e.target.value) })}
                                    />
                                </Grid>
                            )}

                            {form.kind === 'buttons' && (
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                            Botões
                                        </Typography>
                                        <Button
                                            startIcon={<IconPlus size={16} />}
                                            onClick={addButton}
                                            disabled={form.buttons.length >= BUTTONS_MAX}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            Adicionar botão ({form.buttons.length}/{BUTTONS_MAX})
                                        </Button>
                                    </Box>

                                    <Stack spacing={2}>
                                        {form.buttons.map((btn, i) => (
                                            <Card key={i} sx={{ p: 2, borderRadius: '18px' }}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} md={11}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Texto do botão"
                                                            value={btn.title || ''}
                                                            onChange={(e) => updateButton(i, { title: e.target.value })}
                                                        />
                                                    </Grid>

                                                    <Grid item xs={12} md={1}>
                                                        <Button
                                                            color="error"
                                                            onClick={() => removeButton(i)}
                                                            sx={{ height: '100%', minWidth: '100%' }}
                                                        >
                                                            <IconTrash size={18} />
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                            </Card>
                                        ))}
                                    </Stack>
                                </Grid>
                            )}

                            {form.kind === 'list' && (
                                <>
                                    <Grid item xs={12} md={isWApiForm ? 4 : 6}>
                                        <TextField
                                            fullWidth
                                            label="Texto do botão que abre a lista"
                                            helperText="Máximo 20 caracteres (ex: Ver opções)"
                                            inputProps={{ maxLength: 20 }}
                                            value={form.list_button_text}
                                            onChange={(e) => setForm({ ...form, list_button_text: e.target.value })}
                                        />
                                    </Grid>

                                    {isWApiForm && (
                                        <>
                                            <Grid item xs={12} md={4}>
                                                <TextField
                                                    fullWidth
                                                    label="Título da lista"
                                                    helperText="Só usado na API Não Oficial"
                                                    value={form.list_title}
                                                    onChange={(e) => setForm({ ...form, list_title: e.target.value })}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <TextField
                                                    fullWidth
                                                    label="Rodapé (opcional)"
                                                    helperText="Só usado na API Não Oficial"
                                                    value={form.list_footer_text}
                                                    onChange={(e) => setForm({ ...form, list_footer_text: e.target.value })}
                                                />
                                            </Grid>
                                        </>
                                    )}

                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                                Seções e linhas ({totalRows}/{LIST_ROWS_MAX} linhas)
                                            </Typography>
                                            <Button startIcon={<IconPlus size={16} />} onClick={addSection} sx={{ textTransform: 'none' }}>
                                                Adicionar seção
                                            </Button>
                                        </Box>

                                        <Stack spacing={2}>
                                            {form.list_sections.map((section, si) => (
                                                <Card key={si} sx={{ p: 2, borderRadius: '18px' }}>
                                                    <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Título da seção"
                                                            value={section.title}
                                                            onChange={(e) => updateSection(si, { title: e.target.value })}
                                                        />
                                                        <Button color="error" onClick={() => removeSection(si)}>
                                                            <IconTrash size={18} />
                                                        </Button>
                                                    </Stack>

                                                    <Stack spacing={1} pl={2}>
                                                        {(section.rows || []).map((row, ri) => (
                                                            <Grid container spacing={1} key={ri} alignItems="center">
                                                                <Grid item xs={12} md={5}>
                                                                    <TextField
                                                                        fullWidth
                                                                        size="small"
                                                                        label="Título da linha"
                                                                        value={row.title}
                                                                        onChange={(e) => updateRow(si, ri, { title: e.target.value })}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={6}>
                                                                    <TextField
                                                                        fullWidth
                                                                        size="small"
                                                                        label="Descrição (opcional)"
                                                                        value={row.description}
                                                                        onChange={(e) => updateRow(si, ri, { description: e.target.value })}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={1}>
                                                                    <Button color="error" onClick={() => removeRow(si, ri)}>
                                                                        <IconTrash size={16} />
                                                                    </Button>
                                                                </Grid>
                                                            </Grid>
                                                        ))}
                                                        <Button
                                                            size="small"
                                                            startIcon={<IconPlus size={14} />}
                                                            onClick={() => addRow(si)}
                                                            disabled={totalRows >= LIST_ROWS_MAX}
                                                            sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                                                        >
                                                            Adicionar linha
                                                        </Button>
                                                    </Stack>
                                                </Card>
                                            ))}
                                        </Stack>
                                    </Grid>
                                </>
                            )}

                            {form.kind === 'carousel' && (
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                            Cards ({form.carousel_cards.length}/{CAROUSEL_CARDS_MAX})
                                        </Typography>
                                        <Button
                                            startIcon={<IconPlus size={16} />}
                                            onClick={addCard}
                                            disabled={form.carousel_cards.length >= CAROUSEL_CARDS_MAX}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            Adicionar card
                                        </Button>
                                    </Box>

                                    <Stack spacing={2}>
                                        {form.carousel_cards.map((card, ci) => (
                                            <Card key={ci} sx={{ p: 2, borderRadius: '18px' }}>
                                                <Stack direction="row" spacing={2} alignItems="flex-start">
                                                    <Box
                                                        component="label"
                                                        sx={{
                                                            width: 90,
                                                            height: 90,
                                                            flexShrink: 0,
                                                            borderRadius: 2,
                                                            border: '1px dashed rgba(0,0,0,0.3)',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            cursor: 'pointer',
                                                            overflow: 'hidden',
                                                            color: 'text.secondary',
                                                            '&:hover': { borderColor: 'primary.main', color: 'primary.main' }
                                                        }}
                                                    >
                                                        {cardImageFiles[ci] ? (
                                                            <Box
                                                                component="img"
                                                                src={URL.createObjectURL(cardImageFiles[ci])}
                                                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                            />
                                                        ) : card.image ? (
                                                            <Box
                                                                component="img"
                                                                src={card.image}
                                                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                            />
                                                        ) : (
                                                            <>
                                                                <IconPhotoPlus size={20} />
                                                                <Typography variant="caption">Foto</Typography>
                                                            </>
                                                        )}
                                                        <input
                                                            type="file"
                                                            hidden
                                                            accept="image/*"
                                                            onChange={(e) => handleCardImageUpload(ci, e.target.files[0])}
                                                        />
                                                    </Box>

                                                    <Stack spacing={1.5} flex={1}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Texto do card"
                                                            value={card.text}
                                                            onChange={(e) => updateCard(ci, { text: e.target.value })}
                                                        />
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="ou cole a URL de uma imagem"
                                                            placeholder="https://..."
                                                            value={cardImageFiles[ci] ? '' : card.image}
                                                            disabled={!!cardImageFiles[ci]}
                                                            onChange={(e) => updateCard(ci, { image: e.target.value })}
                                                        />

                                                        <Box>
                                                            <Stack
                                                                direction="row"
                                                                justifyContent="space-between"
                                                                alignItems="center"
                                                                mb={1}
                                                            >
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Botões ({(card.buttons || []).length}/{CAROUSEL_CARD_BUTTONS_MAX})
                                                                </Typography>
                                                                <Button
                                                                    size="small"
                                                                    startIcon={<IconPlus size={14} />}
                                                                    onClick={() => addCardButton(ci)}
                                                                    disabled={(card.buttons || []).length >= CAROUSEL_CARD_BUTTONS_MAX}
                                                                    sx={{ textTransform: 'none' }}
                                                                >
                                                                    Adicionar botão
                                                                </Button>
                                                            </Stack>

                                                            <Stack spacing={1}>
                                                                {(card.buttons || []).map((btn, bi) => (
                                                                    <Grid container spacing={1} key={bi} alignItems="center">
                                                                        <Grid item xs={12} md={3}>
                                                                            <TextField
                                                                                select
                                                                                fullWidth
                                                                                size="small"
                                                                                label="Tipo"
                                                                                value={btn.type}
                                                                                onChange={(e) =>
                                                                                    updateCardButton(ci, bi, { type: e.target.value })
                                                                                }
                                                                            >
                                                                                {CARD_BUTTON_TYPE_OPTIONS.map((o) => (
                                                                                    <MenuItem key={o.value} value={o.value}>
                                                                                        {o.label}
                                                                                    </MenuItem>
                                                                                ))}
                                                                            </TextField>
                                                                        </Grid>
                                                                        <Grid
                                                                            item
                                                                            xs={12}
                                                                            md={btn.type === 'CALL' || btn.type === 'URL' ? 4 : 8}
                                                                        >
                                                                            <TextField
                                                                                fullWidth
                                                                                size="small"
                                                                                label="Texto do botão"
                                                                                value={btn.buttonText || ''}
                                                                                onChange={(e) =>
                                                                                    updateCardButton(ci, bi, { buttonText: e.target.value })
                                                                                }
                                                                            />
                                                                        </Grid>
                                                                        {btn.type === 'CALL' && (
                                                                            <Grid item xs={12} md={4}>
                                                                                <TextField
                                                                                    fullWidth
                                                                                    size="small"
                                                                                    label="Telefone"
                                                                                    placeholder="+5511999999999"
                                                                                    value={btn.phone || ''}
                                                                                    onChange={(e) =>
                                                                                        updateCardButton(ci, bi, { phone: e.target.value })
                                                                                    }
                                                                                />
                                                                            </Grid>
                                                                        )}
                                                                        {btn.type === 'URL' && (
                                                                            <Grid item xs={12} md={4}>
                                                                                <TextField
                                                                                    fullWidth
                                                                                    size="small"
                                                                                    label="URL"
                                                                                    value={btn.url || ''}
                                                                                    onChange={(e) =>
                                                                                        updateCardButton(ci, bi, { url: e.target.value })
                                                                                    }
                                                                                />
                                                                            </Grid>
                                                                        )}
                                                                        <Grid item xs={12} md={1}>
                                                                            <Button
                                                                                color="error"
                                                                                size="small"
                                                                                onClick={() => removeCardButton(ci, bi)}
                                                                            >
                                                                                <IconTrash size={16} />
                                                                            </Button>
                                                                        </Grid>
                                                                    </Grid>
                                                                ))}
                                                            </Stack>
                                                        </Box>
                                                    </Stack>

                                                    <Button color="error" onClick={() => removeCard(ci)}>
                                                        <IconTrash size={18} />
                                                    </Button>
                                                </Stack>
                                            </Card>
                                        ))}
                                        {form.carousel_cards.length === 0 && (
                                            <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                                                Nenhum card adicionado ainda
                                            </Typography>
                                        )}
                                    </Stack>
                                </Grid>
                            )}
                        </Grid>
                    </Box>

                    {/* PRÉVIA AO VIVO */}
                    <Box sx={{ width: 420, borderLeft: '1px solid rgba(15,23,42,0.06)', background: '#fff', p: 3, overflow: 'auto' }}>
                        <Typography variant="h5" sx={{ fontWeight: 800, mb: 3 }}>
                            Pré-visualização
                        </Typography>

                        <WhatsappInteractivePreviewCard template={draftTemplate} />

                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <IconSend size={18} />}
                            disabled={!canSave}
                            onClick={save}
                            sx={{ mt: 3, height: 54, borderRadius: '16px', textTransform: 'none', fontWeight: 700 }}
                        >
                            {editingTemplateId ? 'Salvar alterações' : 'Salvar'}
                        </Button>
                    </Box>
                </Box>
            </Dialog>
        </Box>
    );
};

export default ViewWhatsappInteractive;
